import React, { useState, useContext, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";

//pages & includes
import ManageSidebar from "../ManageSidebar";

//functions
import { _t, getCookie } from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//3rd party packages
import { Helmet } from "react-helmet";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

//context consumer
import { SettingsContext } from "../../../../../contexts/Settings";
import IngredientStockPrint from "./IngredientStockPrint";
import { useReactToPrint } from "react-to-print";
import DataTable from "../../../../common/DataTable";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow";

const StockReceivingDeptList = () => {
  const { t } = useTranslation();

  const { loading, setLoading } = useContext(SettingsContext);

  // local list state
  const [receivingList, setReceivingList] = useState(null);
  const [receivingForSearch, setReceivingForSearch] = useState(null);
  const [dataPaginating, setDataPaginating] = useState(false);

  // search state
  const [searchResult, setSearchResult] = useState({
    list: null,
    searched: false,
  });

  // fetch paginated list
  const fetchReceivingList = async (page = 1) => {
    setDataPaginating(true);
    let res;
    try {
      res = await axios.get(
        BASE_URL + `/settings/stock-receiving-dept?page=${page}`,
        { headers: { Authorization: `Bearer ${getCookie()}` } }
      );
    } catch {
      try {
        res = await axios.get(
          BASE_URL + `/settings/stock-recieving-dept?page=${page}`,
          { headers: { Authorization: `Bearer ${getCookie()}` } }
        );
      } catch (err2) {
        console.log("error fetching receiving dept list==>", err2);
        ToastError({ title: _t(t("Please try again")) });
      }
    }

    if (res && res.data) {
      setReceivingList(res.data[0]);
      setReceivingForSearch(res.data[1] || []);
    }
    setDataPaginating(false);
  };

  useEffect(() => {
    fetchReceivingList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    const searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0 || receivingForSearch === null) {
      setSearchResult({ ...searchResult, searched: false });
    } else {
      const searchedList = receivingForSearch.filter((item) => {
        const docNo = (item.document_identity || "").toLowerCase();
        const docDate = (item.document_date || "").toLowerCase();
        const remarks = (item.remarks || "").toLowerCase();
        const fdept = (
          item.from_department?.name ||
          item.from_dept?.name ||
          item.from_dept_name ||
          ""
        ).toLowerCase();
        const tdept = (
          item.to_department?.name ||
          item.to_dept?.name ||
          item.to_dept_name ||
          ""
        ).toLowerCase();
        const branch = (
          item.branch?.name ||
          item.branch_name ||
          ""
        ).toLowerCase();

        return (
          docNo.includes(searchInput) ||
          docDate.includes(searchInput) ||
          remarks.includes(searchInput) ||
          fdept.includes(searchInput) ||
          tdept.includes(searchInput) ||
          branch.includes(searchInput)
        );
      });
      setSearchResult({ ...searchResult, list: searchedList, searched: true });
    }
  };

  // print states and functions
  const [newRow, setNewRow] = useState([]);
  const [postingId, setPostingId] = useState(null);
  const [printData, setPrintData] = useState({
    document_identity: "",
    documentDate: "",
    ref_id: "",
    branch: "",
    fdept: "",
    tdept: "",
    remarks: "",
  });
  const componentRef = useRef();

  const handleOnlyPrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `@page { margin: 15px 25px; }`,
  });

  const handleGetPrintData = async (id) => {
    setPrintData({});
    setNewRow([]);

    let res;
    try {
      res = await axios.get(
        BASE_URL + "/settings/stock-receiving-dept/" + parseInt(id),
        { headers: { Authorization: `Bearer ${getCookie()}` } }
      );
    } catch {
      try {
        res = await axios.get(
          BASE_URL + "/settings/stock-recieving-dept/" + parseInt(id),
          { headers: { Authorization: `Bearer ${getCookie()}` } }
        );
      } catch (err2) {
        console.log("error==>", err2);
        ToastError({ title: _t(t("Please try again")) });
      }
    }

    if (res && res.data) {
      setPrintData({
        ...printData,
        documentDate: new Date(res.data.document_date || res.data.date),
        document_identity: res.data.document_identity,
        issuance_doc_identity: res.data.issuance_doc_identity,
        requisition_doc_identity: res.data.requisition_doc_identity,
        branch: res.data.branch,
        fdept: res.data.from_department || res.data.from_dept,
        tdept: res.data.to_department || res.data.to_dept,
        remarks: res.data.remarks,
      });
      const details =
        res.data.stock_receiving_department_detail ||
        res.data.stock_receiving_dept_detail ||
        res.data.stock_acceptance_detail ||
        res.data.stock_issuance_detail ||
        res.data.details ||
        [];
      if (details.length > 0) {
        setNewRow([...details]);
      }
      handleOnlyPrint();
    }
  };

  const handlePostApi = async (id) => {
    setLoading(true);
    setPostingId(id);
    let res;
    try {
      res = await axios.post(
        BASE_URL + "/settings/receiving-dept-post",
        { id, is_post: 1 },
        { headers: { Authorization: `Bearer ${getCookie()}` } }
      );
    } catch (err) {
      try {
        res = await axios.post(
          BASE_URL + "/settings/stock-receiving-dept-post",
          { id, is_post: 1 },
          { headers: { Authorization: `Bearer ${getCookie()}` } }
        );
      } catch (err2) {
        const message =
          err2.response?.data?.message || err.response?.data?.message;
        ToastError({ title: _t(t(message || "Please try again")) });
        setLoading(false);
        setPostingId(null);
        return;
      }
    }

    if (res && res.data) {
      if (Array.isArray(res.data) && res.data[0]) {
        setReceivingList(res.data[0]);
        setReceivingForSearch(res.data[1] || []);
        setSearchResult({ ...searchResult, list: res.data[1] || [] });
      } else {
        fetchReceivingList(receivingList?.current_page || 1);
      }
      ToastSuccess({ title: _t(t("Stock receiving has been posted")) });
    } else {
      fetchReceivingList(receivingList?.current_page || 1);
    }
    setLoading(false);
    setPostingId(null);
  };

  const handleDeleteConfirmation = (id) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{_t(t("Are you sure?"))}</h1>
            <p className="text-center">{_t(t("You want to delete this?"))}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handleDelete(id);
                  onClose();
                }}
              >
                {_t(t("Yes, delete it!"))}
              </button>
              <button className="btn btn-success ml-2 px-3" onClick={onClose}>
                {_t(t("No"))}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await axios.delete(
        BASE_URL + `/settings/stock-receiving-dept/${id}`,
        { headers: { Authorization: `Bearer ${getCookie()}` } }
      );
      ToastSuccess({
        title: _t(t("Stock receiving has been deleted successfully")),
      });
      fetchReceivingList(receivingList?.current_page || 1);
    } catch (err) {
      const message = err.response?.data?.message;
      ToastError({ title: _t(t(message || "Please try again")) });
    } finally {
      setLoading(false);
    }
  };

  const currentPage = receivingList?.current_page;
  const perPage = receivingList?.per_page;

  const COLUMNS = [
    {
      label: "S/L",
      render: (item, i) =>
        i + 1 + ((currentPage || 1) - 1) * (perPage || 10),
    },
    { label: "Document Number", key: "document_identity" },
    { label: "Document Date", key: "document_date" },
    {
      label: "Branch",
      render: (item) => item.branch?.name || item.branch_name || "-",
    },
    {
      label: "From Department",
      render: (item) =>
        item.from_department?.name ||
        item.from_dept?.name ||
        item.from_dept_name ||
        "-",
    },
    {
      label: "To Department",
      render: (item) =>
        item.to_department?.name ||
        item.to_dept?.name ||
        item.to_dept_name ||
        "-",
    },
    {
      label: "Issue Ref",
      render: (item) => item.issuance_doc_identity || "-",
    },
    {
      label: "Req Ref",
      render: (item) => item.requisition_doc_identity || "-",
    },
    {
      label: "Action",
      render: (item) => (
        <div className="d-flex justify-content-center">
          <button
            className="btn btn-secondary ml-2 rounded-sm del-btn"
            onClick={() => handleGetPrintData(item.id)}
            title="Print"
          >
            <i className="fa fa-print edit-icon"></i>
          </button>
          {item?.is_post === 0 ? (
            <>
              <button
                className="btn btn-dark ml-2 rounded-sm del-btn"
                title="Post receiving"
                style={{ padding: "1px 0px 0px 1px" }}
                onClick={() => handlePostApi(item.id)}
                disabled={postingId === item.id}
              >
                <i className="fa fa-check-square-o" aria-hidden="true"></i>
              </button>
              <button
                className="btn btn-danger ml-2 rounded-sm del-btn"
                title="Delete receiving"
                style={{ padding: "1px 0px 0px 1px" }}
                onClick={() => handleDeleteConfirmation(item.id)}
              >
                <i className="fa fa-trash-o" aria-hidden="true"></i>
              </button>
              <NavLink
                className="btn btn-primary ml-2 rounded-sm"
                to={`/dashboard/manage/stock/stock-receiving-dept-edit/${item.id}`}
                style={{ height: "1.7rem", width: "1.7rem" }}
              >
                <div className="d-flex align-items-center justify-content-center">
                  <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
                </div>
              </NavLink>
            </>
          ) : (
            <NavLink
              className="btn btn-warning ml-2 rounded-sm"
              to={`/dashboard/manage/stock/stock-receiving-dept-view/${item.id}`}
              style={{ height: "1.7rem", width: "1.7rem" }}
            >
              <div className="d-flex align-items-center justify-content-center">
                <i className="fa fa-eye edit-icon" aria-hidden="true"></i>
              </div>
            </NavLink>
          )}
        </div>
      ),
    },
  ];

  const getListData = () => {
    if (searchResult.searched) {
      return searchResult.list || [];
    }
    return receivingList?.data || [];
  };

  const handleFetchData = (page) => {
    fetchReceivingList(page);
  };

  const onClearSearch = () => {
    setSearchResult({ list: null, searched: false });
  };

  return (
    <>
      <Helmet>
        <title>{_t(t("Stock Receiving Dept"))}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div key="fragment3">
                <div className="t-bg-white">
                  <div className="row mx-3 gx-2 align-items-center t-pt-15 t-pb-15">
                    <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                      <ul className="t-list fk-breadcrumb">
                        <li className="fk-breadcrumb__list">
                          <span className="t-link fk-breadcrumb__link text-capitalize">
                            {_t(t("Stock Receiving Dept List"))}
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="col-md-6 col-lg-7">
                      <div className="row gx-3 align-items-center">
                        <div className="col-md-9 t-mb-15 mb-md-0">
                          <div className="input-group">
                            <div className="form-file">
                              <input
                                type="text"
                                className="form-control border-0 form-control--light-1 rounded-left"
                                placeholder={_t(t("Search")) + ".."}
                                onChange={handleSearch}
                                disabled={loading || dataPaginating}
                              />
                            </div>
                            <button
                              className="btn btn-primary rounded-right"
                              type="button"
                              onClick={onClearSearch}
                            >
                              <i className="fa fa-refresh"></i>
                            </button>
                          </div>
                        </div>

                        <div className="col-md-3 text-md-right">
                          <NavLink
                            to="/dashboard/manage/stock/stock-receiving-dept-add"
                            className="btn btn-primary rounded-sm"
                          >
                            <i className="fa fa-plus" aria-hidden="true"></i>{" "}
                            {_t(t("Add New"))}
                          </NavLink>
                        </div>
                      </div>
                    </div>
                  </div>

                  <DataTable
                    columns={COLUMNS}
                    data={getListData()}
                    loading={loading || dataPaginating}
                    totalRows={receivingList?.total || 0}
                    perPage={receivingList?.per_page || 10}
                    currentPage={receivingList?.current_page || 1}
                    handlePageChange={handleFetchData}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <IngredientStockPrint
        name="Stock Receiving Dept"
        newConsumption={printData}
        newRow={newRow}
        print={componentRef}
      />
    </>
  );
};

export default StockReceivingDeptList;
